import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { User } from '@/types'
import { clearSecureRefreshToken } from '@/utils/authSessionStorage'
import { authService } from '@/services/authService'

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  /** Sessão válida só via cookies httpOnly (sem Bearer em memória) */
  sessionUsesCookie: boolean
  /** false até bootstrap (refresh cookie / token em memória) terminar — evita 401 em mount */
  authReady: boolean

  setUser: (user: User) => void
  setToken: (token: string) => void
  login: (user: User, token?: string) => void
  logout: () => Promise<void>
  setRefreshToken: (refreshToken: string) => void
  markAuthSessionReady: () => void
}

function normalizeUser(user: User & { avatar?: string | null }): User {
  const avatarUrl = user.avatarUrl ?? user.avatar ?? undefined
  return {
    ...user,
    role: user.role.toLowerCase() as User['role'],
    avatarUrl: avatarUrl || undefined,
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      sessionUsesCookie: false,
      authReady: false,

      setUser: (user) => set({ user: normalizeUser(user), isAuthenticated: true }),

      markAuthSessionReady: () => set({ authReady: true }),

      setToken: (token) => set({ token }),

      setRefreshToken: (refreshToken) => {
        set({ refreshToken })
      },

      login: (user, token = '') => {
        const hasBearer = Boolean(token)
        set({
          user: normalizeUser(user),
          token: hasBearer ? token : null,
          refreshToken: null,
          isAuthenticated: true,
          sessionUsesCookie: !hasBearer,
          authReady: true,
        })
      },

      logout: async () => {
        // Impede initAuthSession de reidratar via refresh cookie após “Sair”.
        let federated = false
        try {
          sessionStorage.setItem('crm_explicit_logout', '1')
          federated = sessionStorage.getItem('crm_oidc_session') === '1'
          sessionStorage.removeItem('crm_oidc_session')
        } catch {
          /* ignore */
        }
        clearSecureRefreshToken()
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          sessionUsesCookie: false,
          authReady: true,
        })

        // Logout federado: encerra também a sessão no Keycloak (POST + CSRF da API).
        if (federated) {
          try {
            const api = (await import('@/services/api')).default
            const { data } = await api.post<{ success: boolean; data: { logoutUrl: string } }>(
              '/auth/oidc/logout',
            )
            const logoutUrl = data?.data?.logoutUrl
            if (logoutUrl) {
              window.location.href = logoutUrl
              return
            }
          } catch {
            /* fallback: logout local abaixo */
          }
        }

        try {
          await authService.logout()
        } catch {
          /* cookies podem ter sido limpos mesmo com erro de rede */
        }
      },
    }),
    {
      name: 'crm-auth-session',
      storage: createJSONStorage(() => sessionStorage),
      partialize: () => ({}),
      merge: (_persisted, current) => current,
    },
  ),
)
